import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import {
  MEDIA_RESOURCE_TYPES,
  MEDIA_TYPES,
  RESOLUTIONS,
  STREAMING_PROTOCOLS,
  type MediaResourceType,
  type MediaType,
  type Resolution,
  type StreamingProtocol,
} from "@shared/types/content-api";
import {
  emptyMediaDraft,
  emptyResourceDraft,
  type MediaDraft,
  type MediaResourceDraft,
} from "@shared/asset-draft";
import { preferredPreviewResource } from "@shared/stream-preview";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import { CollectionRow, Field, StringListField } from "./editorBits";
import StreamPreview from "./StreamPreview";

/**
 * `medias[] → mediaResources[]` - the real shape, edited as it really is
 * (`BE-4`, re-pointed by `BE-15`).
 *
 * ## What this replaces, and why the flat version could not stay
 *
 * The shipped mockup put `width`, `height`, `audioLanguages`,
 * `subtitleLanguages` and ONE resource list on the channel itself. Upstream
 * every one of those lives on a **media**, of which an asset has several, and
 * that costs the flat model two things it can never say:
 *
 *  - **a trailer is inexpressible.** A main feature and its trailer are two
 *    medias on one asset, differing in type, duration and often language.
 *  - **a 4K main and an HD fallback collapse** into one width and one height,
 *    which is then wrong for at least one of them.
 *
 * A channel normally has exactly one media (`type: "channel"`), which is what
 * made the flattening look harmless right up until a second rendition appeared.
 * The routes now refuse a body in the old shape by name rather than dropping
 * the fields, because a silently dropped `resources` array saves a row that
 * plays nothing behind a green tick.
 *
 * ## DRM is one boolean here, and that is not an oversight
 *
 * Production does not store licence URLs; it **mints them per playback
 * request** (an ES256 Verimatrix JWT with a 30-minute life, from the asset's
 * `alternativeId`). There is no scheme list, no licence URL and no certificate
 * to author - `BE-4` shipped all three and none of them had an upstream home.
 * The two authoring-time checks that fall out of it are in
 * `assetPlaybackBlockers`, and the dialog draws them.
 *
 * ## The units are in the labels, on purpose
 *
 * `durationMillis` is MILLISECONDS and the asset-level `duration` beside it is
 * SECONDS. The portal's own Duration box is free text with no unit label at all
 * feeding an `Int` column, which is why the catalogue is assumed to hold mixed
 * units until somebody counts them.
 */

function ResourceRow({
  resource,
  index,
  siblings,
  onChange,
  onRemove,
}: {
  resource: MediaResourceDraft;
  index: number;
  /** Every resource on this media, so a refusal can name a better one. */
  siblings: readonly MediaResourceDraft[];
  onChange: (patch: Partial<MediaResourceDraft>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation(["content", "common"]);
  return (
    <Stack
      spacing={1}
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: tokens.brandTintSoft,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ md: "flex-start" }}
      >
        <Field
          select
          label={t("mediasEditor.delivery")}
          value={resource.type}
          onChange={(e) =>
            onChange({ type: e.target.value as MediaResourceType })
          }
          sx={{ width: 140 }}
        >
          {MEDIA_RESOURCE_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {t(`mediasEditor.resourceType.${type}`)}
            </MenuItem>
          ))}
        </Field>
        <Field
          select
          label={t("mediasEditor.protocolLabel")}
          value={resource.protocol}
          onChange={(e) =>
            onChange({ protocol: e.target.value as StreamingProtocol })
          }
          sx={{ width: 180 }}
          helperText={
            resource.protocol === "other"
              ? t("mediasEditor.protocolOtherHint")
              : undefined
          }
        >
          {STREAMING_PROTOCOLS.map((protocol) => (
            <MenuItem key={protocol} value={protocol}>
              {t(`mediasEditor.protocol.${protocol}`)}
            </MenuItem>
          ))}
        </Field>
        <Field
          label={t("mediasEditor.streamUrl")}
          placeholder="https://cdn.example.com/live.m3u8"
          value={resource.url}
          onChange={(e) => onChange({ url: e.target.value })}
          sx={{ flex: "1 1 auto", minWidth: 220 }}
          inputProps={{ "data-testid": `resource-url-${index}` }}
        />
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={resource.isEncrypted}
              onChange={(e) => onChange({ isEncrypted: e.target.checked })}
            />
          }
          label={
            <Typography variant="meta">
              {t("mediasEditor.encrypted")}
            </Typography>
          }
        />
        <Button
          size="small"
          onClick={onRemove}
          sx={{ color: tokens.error, mt: 1, minHeight: 0 }}
        >
          {t("common:buttons.remove")}
        </Button>
      </Stack>

      {/* Under the fields rather than beside them: the player is 16:9 and the
          row is already five controls wide. Every other verdict in this dialog
          is derived from metadata — this is the one place the editor can show
          that a URL leads to actual video. */}
      <StreamPreview resource={resource} siblings={siblings} />
    </Stack>
  );
}

/**
 * The media's own preview: the one an operator reaches for.
 *
 * `preferredPreviewResource` picks the rendition the estate actually plays —
 * a clear HLS first, then anything else playable, then the first row so the
 * refusal shown is about something real rather than about an empty list.
 *
 * A media with no streams at all draws a sentence instead of a dead button,
 * because in the catalogue this CMS is bound to that is the COMMON case, and a
 * missing control reads as a missing feature.
 */
function MediaPreviewHeader({ media }: { media: MediaDraft }) {
  const { t } = useTranslation("content");
  // Native HLS is a browser fact the player probes for itself; assuming "no"
  // here only affects WHICH resource is preferred, and hls.js plays everything
  // native HLS would.
  const chosen = preferredPreviewResource(media.mediaResources, false);
  if (!chosen) {
    return (
      <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
        {t("mediasEditor.noStreamToPreview")}
      </Typography>
    );
  }
  return <StreamPreview resource={chosen} siblings={media.mediaResources} />;
}

export default function MediasEditor({
  medias,
  defaultType,
  onChange,
}: {
  medias: MediaDraft[];
  /** `channel` for a channel, `main` for a title - the one asset-shaped choice. */
  defaultType: MediaType;
  onChange: (next: MediaDraft[]) => void;
}) {
  const { t } = useTranslation(["content", "common"]);
  function update(index: number, patch: Partial<MediaDraft>) {
    onChange(medias.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  }

  return (
    <Stack spacing={2}>
      {/* Says the quiet part, because two thirds of the catalogue this CMS is
          bound to has no media at all: an asset with nothing to play shows no
          preview, and "no button" must not read as "the feature is missing". */}
      {medias.length === 0 && (
        <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
          {t("mediasEditor.noMedia")}
        </Typography>
      )}

      {medias.map((media, index) => (
        <CollectionRow
          key={media.mediaId ?? `new-${index}`}
          title={
            media.name.trim() ||
            (media.mediaId === null
              ? t("mediasEditor.mediaTitleNew", {
                  type: t(`mediasEditor.mediaType.${media.type}`),
                })
              : t("mediasEditor.mediaTitleId", {
                  type: t(`mediasEditor.mediaType.${media.type}`),
                  id: media.mediaId,
                }))
          }
          removeLabel={t("mediasEditor.removeMedia")}
          onRemove={() => onChange(medias.filter((_, i) => i !== index))}
        >
          <Stack spacing={2}>
            {/* The preview belongs HERE as well as on each stream, and burying
                it only in the stream rows was the mistake: a media is what an
                operator thinks they are checking ("does this title play?"),
                and the row it lives on is four scrolls down inside a section
                most people never open. This opens the rendition the estate
                actually plays - HLS first - and says so when there is none. */}
            <MediaPreviewHeader media={media} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <Field
                select
                label={t("common:labels.type")}
                value={media.type}
                onChange={(e) =>
                  update(index, { type: e.target.value as MediaType })
                }
              >
                {MEDIA_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {t(`mediasEditor.mediaType.${type}`)}
                  </MenuItem>
                ))}
              </Field>
              <Field
                label={t("common:labels.name")}
                placeholder={t("mediasEditor.namePlaceholder")}
                value={media.name}
                onChange={(e) => update(index, { name: e.target.value })}
              />
              <Field
                select
                label={t("mediasEditor.resolution")}
                value={media.videoResolution}
                onChange={(e) =>
                  update(index, {
                    videoResolution: e.target.value as Resolution,
                  })
                }
              >
                {RESOLUTIONS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Field>

              <Field
                label={t("mediasEditor.width")}
                value={media.videoWidth}
                onChange={(e) => update(index, { videoWidth: e.target.value })}
              />
              <Field
                label={t("mediasEditor.height")}
                value={media.videoHeight}
                onChange={(e) => update(index, { videoHeight: e.target.value })}
              />
              <Field
                label={t("mediasEditor.duration")}
                placeholder={t("mediasEditor.durationPlaceholder")}
                value={media.durationMillis}
                onChange={(e) =>
                  update(index, { durationMillis: e.target.value })
                }
                helperText={t("mediasEditor.durationHint")}
              />

              <Field
                label={t("mediasEditor.spokenLanguage")}
                placeholder="sv"
                value={media.spokenLanguage}
                onChange={(e) =>
                  update(index, { spokenLanguage: e.target.value })
                }
              />
            </Box>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={4}
              sx={{ pt: 1 }}
            >
              <StringListField
                label={t("mediasEditor.audioLanguages")}
                hint={t("mediasEditor.audioLanguagesHint")}
                value={media.audioLanguages}
                placeholder="sv"
                onChange={(next) => update(index, { audioLanguages: next })}
              />
              <StringListField
                label={t("mediasEditor.subtitleLanguages")}
                hint={t("mediasEditor.subtitleLanguagesHint")}
                value={media.subtitleLanguages}
                placeholder="en"
                onChange={(next) => update(index, { subtitleLanguages: next })}
              />
              <Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={media.multiChannelAudio}
                      onChange={(e) =>
                        update(index, { multiChannelAudio: e.target.checked })
                      }
                    />
                  }
                  label={
                    <Typography variant="meta">
                      {t("mediasEditor.multiChannelAudio")}
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={media.hardOfHearing}
                      onChange={(e) =>
                        update(index, { hardOfHearing: e.target.checked })
                      }
                    />
                  }
                  label={
                    <Typography variant="meta">
                      {t("mediasEditor.hardOfHearing")}
                    </Typography>
                  }
                />
              </Stack>
            </Stack>

            <Box>
              <Typography
                variant="meta"
                sx={{ fontWeight: "fontWeightBold", color: tokens.ink, mb: 1 }}
              >
                {t("mediasEditor.streams")}
              </Typography>
              <Typography
                variant="micro"
                sx={{ color: tokens.inkMuted, mb: 1.5 }}
              >
                {t("mediasEditor.streamsHint")}
              </Typography>
              <Stack spacing={1.5}>
                {media.mediaResources.map((resource, resourceIndex) => (
                  <ResourceRow
                    key={resource.mediaResourceId ?? `new-${resourceIndex}`}
                    resource={resource}
                    index={resourceIndex}
                    siblings={media.mediaResources}
                    onChange={(patch) =>
                      update(index, {
                        mediaResources: media.mediaResources.map((r, i) =>
                          i === resourceIndex ? { ...r, ...patch } : r
                        ),
                      })
                    }
                    onRemove={() =>
                      update(index, {
                        mediaResources: media.mediaResources.filter(
                          (_, i) => i !== resourceIndex
                        ),
                      })
                    }
                  />
                ))}
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ alignSelf: "flex-start" }}
                  data-testid={`add-resource-${index}`}
                  onClick={() =>
                    update(index, {
                      mediaResources: [
                        ...media.mediaResources,
                        emptyResourceDraft(),
                      ],
                    })
                  }
                >
                  {t("mediasEditor.addStream")}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </CollectionRow>
      ))}

      <Button
        size="small"
        variant="outlined"
        sx={{ alignSelf: "flex-start" }}
        data-testid="add-media"
        onClick={() => onChange([...medias, emptyMediaDraft(defaultType)])}
      >
        {t("mediasEditor.addMedia")}
      </Button>
    </Stack>
  );
}
